import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

import type {
  PassengerRead,
  PayerOrganizationRead,
} from 'src/common/external/bambi-api/bambiApi';

/**
 * The slice-agnostic view of the pricing sub-form's shared inputs (selected
 * passenger, selected payer, and edit state). Each form (add-a-trip, trip
 * subscription) provides this from its own slice so the shared pricing
 * components don't have to know which form they're rendered in.
 */
export interface PricingFormContextValue {
  selectedPassenger: PassengerRead | null | undefined;
  /** True when editing an existing record (trip or subscription). */
  isEditing: boolean;
  /**
   * True when the underlying record has an intermediary, which locks the payer
   * select. Trip subscriptions have no intermediary, so this is always false there.
   */
  hasIntermediary: boolean;
  /** Passenger/payer ids on the saved record, used to detect user changes. */
  savedPassengerId?: string;
  savedPayerId?: string;
  selectedPayer: PayerOrganizationRead | null;
  onPayerSelected: (payer: PayerOrganizationRead | null) => void;
}

export const defaultPricingFormContextValue: PricingFormContextValue = {
  selectedPassenger: undefined,
  isEditing: false,
  hasIntermediary: false,
  savedPassengerId: undefined,
  savedPayerId: undefined,
  selectedPayer: null,
  onPayerSelected: () => {},
};

const PricingFormContext = createContext<PricingFormContextValue>(
  defaultPricingFormContextValue
);

export function usePricingFormContext(): PricingFormContextValue {
  return useContext(PricingFormContext);
}

/**
 * Generic provider — each form builds a thin wrapper around this that reads
 * from its own slice (see AddTripPricingFormProvider in the add-trip feature
 * and TripSubscriptionPricingFormProvider in the trip-subscriptions feature).
 */
export function PricingFormProvider({
  value,
  children,
}: {
  value: PricingFormContextValue;
  children: ReactNode;
}) {
  return (
    <PricingFormContext.Provider value={value}>
      {children}
    </PricingFormContext.Provider>
  );
}
